import {savedBrandAdReadRequest} from './savedBrandAdRequest';
import {isScopedReferenceEdit} from './variationRequestConstraints';
import {creativeRequestCount} from './creativeAllocation';
import {chatStylePreference} from './chatPreference';
import {pendingAdRequest} from './pendingAdRequest';
import {storedBrandAdRemakeRequest} from './storedBrandAdIntent';
function defersGeneration(text:string):boolean {
  text=text.replace(/[’‘]/g,"'");
  return /\b(?:not\s+yet(?=\s*(?:[,.!?;]|$))|(?:don['’]?t|do\s+not)\s+(?:do|run|start|apply|make|create|generate|render|remake|clone|copy|adapt|edit|change)(?:\s+(?:it|this|that|them|anything|images?|ads?))?\s+yet|wait\s+(?:for\s+(?:my|me)|until)|hold\s+off)\b/i.test(text);
}

function explicitlyNonGenerating(msg:string):boolean {
  return defersGeneration(msg)||/\b(?:do\s+not|don['’]?t|never)\s+(?:generate|render|create|edit|change)\s+(?:anything|any\s+images?|images?|ads?|this|it)\b/i.test(msg)||/\b(?:only|just)\s+(?:(?:want|give|provide|share)\s+)?(?:(?:me|your)\s+)?(?:advice|feedback|ideas?|discussion)\b/i.test(msg);
}

/** A counted iteration command owns any trailing clarification question. */
function countedIterationRequest(msg: string): number | undefined {
  if (explicitlyNonGenerating(msg)) return;
  const action=msg.trim().replace(/^(?:please\s+)?(?:can|could|would|will)\s+you\s+(?:please\s+)?/i,'').replace(/^please\s+/i,'');
  const match=/^(?:give\s+me|make|create|generate|produce|build)\s+(?:me\s+)?(\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|twenty|thirty|forty|fifty)\s+(?:more\s+)?iterations?\b(?!\s+(?:ideas?|suggestions?))/i.exec(action);
  if (!match || match[1]==='0') return;
  return Math.min(100,creativeRequestCount(match[1]+' ads'));
}

/** Explicit edits of a supplied image are executable requests, including polite questions. */
export function looksLikeImageEditRequest(msg: string, hasSelectedImage = false): boolean {
  if (explicitlyNonGenerating(msg)) return false;
  const text = msg.trim().replace(/^(?:please\s+)?(?:can|could|would|will)\s+you\s+(?:please\s+)?/i, '').replace(/^please\s+/i, '').replace(/^just\s+(?=(?:make|use|apply|update|change|edit|replace|swap|remove|add|put|place|insert|blend|mix|combine|merge)\b)/i, '');
  if (/\b(?:do\s+not|don['’]?t|never)\s+(?:generate|render|create|edit|change)\s+(?:anything|any\s+images?|images?|ads?|this|it)\b/i.test(text)
    || /\b(?:only|just)\s+(?:want\s+)?(?:your\s+)?(?:advice|feedback|ideas?|discussion)\b/i.test(text)
    || /\b(?:in|to|on)\s+(?:our|the|my|this|that)\s+(?:brief|plan|document|notes?|prompt)\b/i.test(text)) return false;
  if (hasSelectedImage && isScopedReferenceEdit(msg)) return true;
  if (hasSelectedImage && countedIterationRequest(msg) !== undefined) return true;
  if (/^make\s+(?:(?:the|this|that|its)\s+)?(?:background|logo|headline|text|copy|product|packaging|image|photo|it|this|that|them)\s+\S/i.test(text)
      && !/\b(?:ads?|variations?|versions?|campaign|concepts?)\b/i.test(text)) return true;
  if (/^(?:use|apply)\b/i.test(text)) return /^(?:use|apply)\s+(?:our|the|my|brand|approved)\b[^.!?]*\b(?:fonts?|typography|colors?|colours?)\b/i.test(text);
  return /^(?:update|change|edit|replace|swap|remove|add|put|place|insert|blend|mix|combine|merge)\b/i.test(text)
    && (hasSelectedImage || /\b(?:fonts?|typography|headlines?|text|copy|badges?|backgrounds?|images?|photos?|pictures?|characters?|persons?|people|person|woman|man|model|subject|shot|colors?|colours?|logos?|packaging|this|these|them|together)\b/i.test(text));
}

/** Peel off a polite request wrapper, keeping the user's action and count. */
export function generationActionText(msg: string): string {
  const text = msg.replace(/^((?:please\s+)?(?:make|create|generate|build|produce|give\s+me|gimme)\s+(?:me\s+)?\d{1,3})\s+m{2,}ore\b/i, '$1 more').replace(/\b(?:varations?|variaitons?|variatons?|variations?)\b/gi, 'variations').trim();
  const polite = text.match(/(?:^|[,!.]\s*)(?:please\s+)?(?:can|could|would|will)\s+you\s+(?:please\s+)?((?:make|create|generate|build|produce|give\s+me|gimme|copy|clone|adapt|remake|redo|remix|resize|reformat)\b[\s\S]*)/i);
  if (polite && /\b(?:ads?|images?|creatives?|variations?|versions?|this|these|it)\b/i.test(polite[1])) return polite[1].replace(/\?\s*$/, '').trim();
  const desire=text.match(/^i\s+(?:want|need|would\s+like|would\s+love)\s+(?:you\s+)?to\s+((?:make|create|generate|render|build|produce|clone|copy|adapt|edit)\b[\s\S]*)/i);
  if(desire)return desire[1].replace(/\?\s*$/,'').trim();
  const command=text.replace(/^(?:ok(?:ay)?|no(?:\s+no)?|actually|yes|yep|sure)[,!.]?\s+(?=(?:make|create|generate|render|build|produce|give\s+me|gimme|clone|copy|adapt|remake|redo|remix|edit)\b)/i,'');
  return /^(?:make|create|generate|render|build|produce|give\s+me|gimme|clone|copy|adapt|remake|redo|remix|edit)\b/i.test(command)?command.replace(/\?\s*$/,''):command;
}
export function explicitAdPlanningRequest(message:string):boolean {
  const text=message.trim().replace(/^(?:please\s+)?(?:can|could|would|will)\s+you\s+(?:please\s+)?/i,'').replace(/^please\s+/i,'');
  return /^(?:help\s+me\s+)?(?:plan|outline|strategi[sz]e)\s+(?:(?:some|a|the|\d+|one|two|three|four|five|new|ad|marketing)\s+)*(?:ads?|concepts?|angles?|campaigns?)\b/i.test(text);
}

/** Current discussion wins over an old picker or selected image. */
export function isExplicitConversation(msg: string): boolean {
  if (savedBrandAdReadRequest(msg)) return true;
  if (chatStylePreference(msg)) return true;
  if (isTextIdeationRequest(msg)) return true;
  if (explicitlyNonGenerating(msg)) return true;
  if (looksLikeImageEditRequest(msg) || countedIterationRequest(msg) !== undefined) return false;
  if (generationActionText(msg) !== msg.trim() && /^(?:make|create|generate|build|produce|give\s+me|gimme|copy|clone|adapt|remake|redo|remix|resize|reformat)\b/i.test(generationActionText(msg))) return false;
  const text = msg.replace(/\[(?:Attached|Reference) image(?: to clone)?:[^\]]+\]/gi, "").trim();
  return /^(?:please\s+)?(?:what|which|who|where|when|why|how|tell|explain|describe|discuss|review|critique|analyse|analyze|brainstorm)\b/i.test(text)
    || /^(?:please\s+)?(?:give\s+me|suggest|share|help\s+me(?:\s+brainstorm)?)\s+(?:some\s+)?(?:ideas?|suggestions?|advice|feedback|inspiration)\b/i.test(text)
    || /\?\s*$/.test(text);
}

/** Classify the requested output, not ad nouns in its subject/context. */
export function isTextIdeationRequest(message: string): boolean {
  const action = generationActionText(message).replace(/^(?:please\s+)?(?:can|could|would|will)\s+you\s+(?:please\s+)?/i, '').replace(/^please\s+/i, '').trim();
  // A separate execution clause retains its normal route. "ideas for this ad"
  // alone describes text; "then make five ads" supplies execution intent.
  if (/(?:[.!;]\s*|\b(?:and\s+then|then|and)\s+)(?:please\s+)?(?:make|create|generate|render|build|edit|apply)\s+(?:(?:\d+|one|two|three|four|five|these|those|the|this)\s+)?(?:ads?|images?|creatives?|them|it|these|those|this)\b/i.test(action)) return false;
  return /^(?:give\s+me|gimme|suggest|share|write|draft|brainstorm|generate|create|make)\s+(?:me\s+)?(?:(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|some|a\s+few)\s+)?(?:more\s+|new\s+|alternative\s+|different\s+)?(?:(?:ideas?|suggestions?|alternatives?|options?)\b|(?:ad\s+)?(?:copy|headlines?|hooks?|captions?)\b(?:\s+(?:ideas?|alternatives?|options?|variations?|versions?))?)(?:\b|[.!?])/i.test(action);
}

export function looksLikeGenerationRequest(
  msg: string,
  conversationHistory?: Array<{ role: string; content?: unknown }>,
  hasSelectedImage = false,
): boolean {
  if(savedBrandAdReadRequest(msg))return false;
  if(storedBrandAdRemakeRequest(msg))return true;
  const pending=pendingAdRequest(msg,conversationHistory);
  if(pending)return pending.status==='ready';
  if (hasSelectedImage && looksLikeImageEditRequest(msg, true)) return true;
  if (!msg || isExplicitConversation(msg)) return false;
  if (looksLikeImageEditRequest(msg) || looksLikeReferenceCloneRequest(msg)) return true;
  const m = generationActionText(msg).toLowerCase().trim();

  // CONTEXT AWARENESS — check this FIRST, before any "informational" filter.
  // If the prior assistant turn surfaced a pill picker / choice question
  // (competitor picker, product picker, "which X do you want?"), then a
  // short user response naming an entity is a CONTINUATION of the pending
  // generation intent — NOT an informational query.
  //
  // User-reported bug: agent asked "which competitor?" with pills, user
  // typed "use Moon Juice", server-side guard blocked the playbook call
  // because "use Moon Juice" didn't match the imperative regex. Agent told
  // user to "rephrase as 'make 5 ads like Moon Juice'" — exactly the kind
  // of stupid behavior the user complained about ("feel like Claude, not
  // a switch statement"). Context-aware check fixes this class entirely.
  if (conversationHistory && conversationHistory.length > 0) {
    // Find the most recent assistant message
    let lastAssistantText = "";
    for (let i = conversationHistory.length - 1; i >= 0; i--) {
      const turn = conversationHistory[i];
      if (turn.role === "assistant") {
        const c = turn.content;
        lastAssistantText = typeof c === "string"
          ? c
          : Array.isArray(c)
            ? c.map((b) => (typeof b === "object" && b && "text" in b ? String((b as { text?: string }).text ?? "") : "")).join(" ")
            : "";
        break;
      }
    }
    if (lastAssistantText) {
      const lastLower = lastAssistantText.toLowerCase();

      // v2.26 — affirmative reply to an explicit assistant OFFER to do
      // creative work. Observed failure (Jun 12): agent answered an edit
      // request with "Would you like me to create that remix variation for
      // you?", user typed "yes", and because "yes" matches no generation
      // pattern the hallucination guard stayed disarmed — the model then
      // narrated "Perfect! I've created a remix variation..." with ZERO
      // tool call and nothing happened on canvas. An affirmative after an
      // offer IS the generation request.
      const offeredCreativeWork =
        /\b(?:would\s+you\s+like\s+me\s+to|want\s+me\s+to|shall\s+i|should\s+i|i\s+can)\b[^?]{0,120}\b(?:create|generate|make|run|build|remix|regenerate|redo|fix|edit|apply|produce)\b/.test(lastLower);
      const isAffirmativeReply =
        /^\s*(?:yes|yep|yeah|ya|sure|ok(?:ay)?|please(?:\s+do)?|do\s+it|go(?:\s+ahead)?|sounds\s+good|let'?s\s+(?:do\s+it|go))[\s.!]*$/.test(m);
      if (offeredCreativeWork && isAffirmativeReply) return true;

      // Detect pending-choice indicators from the assistant's prior turn.
      const isPendingChoice =
        /awaiting[_\s]?competitor[_\s]?choice/.test(lastLower) ||
        /which\s+competitor/.test(lastLower) ||
        /which\s+(?:one|product|brand|style|angle|playbook)/.test(lastLower) ||
        /pick\s+(?:a|one|which)/.test(lastLower) ||
        /choose\s+(?:a|one|from)/.test(lastLower) ||
        /select\s+(?:a|one|which)/.test(lastLower) ||
        // Pill-picker text often lists options on separate lines
        (/\boption|\bchoice|\bcandidate/.test(lastLower) && /[-*•]\s/.test(lastAssistantText));
      if (isPendingChoice && m.length < 80) {
        // Short response to a pending choice — treat as a generation
        // continuation. Patterns: "use X", "X", "go with X", "pick X",
        // "let's try X", "yes X", "the X one", or bare entity names.
        const isShortChoiceResponse =
          /^(?:use|do|go\s+with|let'?s\s+(?:use|go|try|do)|pick|choose|select|try|how\s+about|the|that)\b/.test(m) ||
          // Bare entity name (no verbs, just words/numbers/parens/hyphens)
          /^[a-z0-9][a-z0-9\s\-\(\)\.&]{0,38}$/.test(m);
        if (isShortChoiceResponse) return true;
      }
    }
  }

  // Hard filter — informational / question patterns are NEVER generation
  // requests. The hallucination retry must not fire on these, otherwise
  // it'd force a tool call on "what are my angles?" / "how does X work?"
  // / "list my brands" / etc. Check this BEFORE any generation heuristic.
  const questionPatterns = [
    /^\s*(what|what's|whats|which|who|where|when|why|how|how's|hows|are|is|do|does|did|can|could|will|would|should|may|might|tell|explain|describe|list|show\s+me\s+the\s+(?:list|options|available|types?|kinds?))\b/,
    /\?\s*$/, // ends with question mark
    /^\s*(hi|hey|hello|thanks|thank\s+you|ok|okay|cool|nice|got\s+it|sure)\b[^.!?]*$/, // greetings / acks
  ];
  for (const pat of questionPatterns) {
    if (pat.test(m)) return false;
  }
  if(/^(?:\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten)\s+(?:(?:[\w-]+)\s+){0,3}(?:ads?|images?|creatives?)\s*(?:please[.!]*)?\s*(?:$|(?:for|with|using)\b)/i.test(m))return true;
  // Counted continuation orders can omit the ad noun. Keep this after question guards.
  if (/^(?:please\s+)?(?:make|create|generate|build|produce|give\s+me|gimme)\s+(?:me\s+)?\d{1,3}\s+more\b/.test(m)) return true;
  // v7.32 — an explicit imperative make/create-N-ads head IS a generation
  // request no matter what topic words follow. "make 5 ads … us vs them
  // comparison style" matched the vs/comparison question-pattern below and
  // got treated as INFORMATIONAL: the guard blocked every campaign playbook
  // and the agent improvised ONE raw generate_ad_image (Dan's p3 loop run
  // delivered 1/5). Question-start and "?" endings were already handled
  // above, so this can't re-arm on "should I make 5 ads?".
  if (/\b(?:make|create|generate|build|produce|design|whip\s+up|cook\s+up|give\s+me|gimme)\s+(?:me\s+)?\d{1,3}\s+(?:[\w'’-]+\s+){0,4}(?:ads?|creatives?|variations?|versions?)\b/.test(m)) {
    return true;
  }
  // vs/comparison/difference asks WITHOUT an imperative-count head stay
  // informational ("what's the difference between X and Y").
  if (/\b(vs|versus|compare|comparison|difference\s+between)\b/.test(m)) return false;

  // Verb + noun combo — clear imperative generation request. Expanded to
  // include "use" (e.g. "use Moon Juice", "use the AG1 winning ads") which
  // is a natural imperative verb users reach for when responding to picker
  // questions.
  if (/^(?:please\s+)?(make|create|generate|render|design|build|whip|give\s+me|do\s+me|do\s+up|build\s+me|produce|use|run|fire)\b/.test(m) &&
      /\b(ads?|creatives?|images?|variations?|angles?|versions?|remixes?|formats?|aspect|stories|reels|posts?|playbook|campaign|competitor|competitors?)\b/.test(m)) {
    return true;
  }
  // Direct playbook trigger phrases
  if (/\b(remake|redo|regenerate|reformat|remix|fix\s+(?:the\s+)?(?:packaging|product))\b/.test(m)) return true;
  // "N variations / N angles / N versions / N of each" — but only if it
  // looks like an imperative ("make 5 ads", "give me 3 variations"), not
  // a question ("how many angles do I have?"). The question filter above
  // already covers most of these — this is the catch-all.
  if (/\b(make|create|generate|give|build|do)\s+\d+\s+(?:more\s+)?(?:variations?|angles?|versions?|ads?|creatives?|of\s+each)\b/.test(m)) return true;
  // "make this / do this / clone this / reformat this"
  if (/\b(make|do|clone|reformat|remake)\s+(this|these|that|those|it)\b/.test(m)) return true;
  if (/^(?:i\s+)?(?:want|need|would\s+like)\s+(?:(?:\d{1,3}|a|an|some|one|two|three|four|five)\s+)?(?:(?:new|fresh|more|lifestyle|ugc|studio|product)\s+)*(?:ads?|images?|creatives?|variations?)\b/.test(m)) return true;
  return false;
}


export function looksLikeVariationRequest(msg: string): boolean {
  const m = generationActionText(msg || "").trim().toLowerCase();
  if (!m || isExplicitConversation(msg)) return false;
  if (countedIterationRequest(msg) !== undefined) return true;
  if (/^\s*(what|which|how|why|when|where|who|should\s+i|do\s+you|can\s+you|could\s+you|is\s+it|are\s+these|tell|explain|list)\b/.test(m)) return false;
  if (/\?\s*$/.test(m)) return false;
  return (
    /\bmore\s+like\s+(this|these|that|it|those)\b/.test(m) ||
    /\b\d{1,3}\s+more\b/.test(m) ||
    /\b(?:\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|twenty)\s+(?:(?:new|different|ad|copy|headline)\s+)*(?:variations?|versions?|angles?|remixes?)\b/.test(m) ||
    /\b(variations?|versions?)\s+of\s+(this|these|each|it|that|those|all)\b/.test(m) ||
    /\b(remake|redo|regenerate|re-?run|remix|re-?create)\s+(this|these|it|that)\b/.test(m) ||
    /\b(another|more)\s+(one|version|like\s+(?:this|these|it)|of\s+(?:this|these|it))\b/.test(m) ||
    /\b(make|create|generate|give|gimme|do|build|whip\s+up)\b.*\b(like|of)\s+(this|these|that|it|those|the\s+(?:first|second|former|latter)\s+(?:one|ad|image))\b/.test(m)
  );
}

/** Copying a selected ad means adapting its layout to the current offering. */
export function looksLikeReferenceCloneRequest(message: string): boolean {
  if (isExplicitConversation(message)) return false;
  const text = generationActionText(message).replace(/^please\s+/i, '').replace(/\[(?:Attached|Reference) image(?: to clone)?:[^\]]+\]/gi, '').trim();
  if (/\b(?:do\s+not|don['’]?t|never)\b|\b(?:only|just)\s+(?:give|want|provide)\s+(?:your\s+)?(?:advice|feedback)|\b(?:into|in|to)\s+(?:our|the|my|this)\s+(?:brief|plan|document|notes?)\b/i.test(text)) return false;
  return /^(?:copy|clone|adapt)\s+(?:(?:this|that|these|those|the\s+selected)\s+(?:ads?|images?|creatives?)|(?:this|that|these|those|it)\b)/i.test(text)
    || /^(?:make|create|generate)\s+(?:(?:this|that|the\s+selected)\s+(?:ad|image|creative)\b|(?:\d{1,3}\s+)?(?:copies|clones)\s+of\s+(?:this|that|these|those|the\s+selected)\b)/i.test(text);
}

export function selectedReferenceCloneAction(message: string, sources: Array<{clientId: string}> | undefined) {
  if (!sources?.length || !looksLikeReferenceCloneRequest(message)) return null;
  const text = generationActionText(message);
  const count = text.match(/\b(\d{1,3})\s+(?:copies|clones|ads?|versions?|variations?|times)\b/i)
    ?? text.match(/\b(?:copy|clone|adapt)\s+(\d{1,3})\b/i);
  return { count: count ? Math.min(50, Math.max(1, Number(count[1]))) : 1, sourceImageIds: [...new Set(sources.map(source => source.clientId))] };
}

export function selectedVariationAction(msg: string, sources: Array<{clientId: string}> | undefined) {
  if (!sources?.length || !looksLikeVariationRequest(msg)) return null;
  const iterationCount=countedIterationRequest(msg);
  const text = generationActionText(msg);
  const match = text.match(/\b(?:make|create|generate|give\s+me|gimme|do|build|want|need)\s+(?:me\s+)?(\d{1,3})\b/i)
    || text.match(/\b(\d{1,3})\s+(?:more|variations?|versions?|angles?)\b/i);
  const single = /\b(?:remake|redo|regenerate|re-?run|re-?create|try\s+again|another\s+one|one\s+more|another\s+version|do\s+another|just\s+one)\b/i.test(text);
  return { count: iterationCount ?? (match ? Math.min(100, Math.max(1, Number(match[1]))) : single ? 1 : creativeRequestCount(text.replace(/\b(?:variations?|versions?|angles?|remixes?)\b/gi,'ads').replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|twenty|thirty|forty|fifty)\s+(?=like\b)/gi,'$1 ads '),5)), sourceImageIds: [...new Set(sources.map(source => source.clientId))] };
}

export function conceptsFirstEligible(ctx: { referenceImages?: string[]; selectedAspectRatios?: string[] }, count: number): boolean {
  return Number.isInteger(count) && count >= 1 && count <= 100;
}

export function looksLikeFreshCampaignRequest(msg: string): boolean {
  const m = generationActionText(msg || "").trim().toLowerCase();
  if (!m || isExplicitConversation(msg)) return false;
  // v4.81 — competitor asks belong to run_competitor_ads (its own fast-path
  // runs first); NEVER swallow them into campaign-v2. Typo-tolerant stem.
  // v7.32 — NEGATION-AWARE: "don't name any competitor brands directly" is a
  // CONSTRAINT on a fresh batch, not a competitor-clone ask — it was falling
  // to the LLM router (Dan's p3 conquest run). Only exclude when the
  // competitor mention is not negated.
  if (
    (/\bcomp[ei]t\w*/.test(m) || /\brivals?\b/.test(m)) &&
    !/\b(?:don'?t|do\s+not|never|no|without|avoid)\b[^.!?]{0,50}\bcomp[ei]t\w*/.test(m)
  ) return false;
  // v6.83 — concept/angle asks belong to the plan-angles path ("make 5 ad
  // concepts for xmas" ran campaign-v2 because "make 5 ad…" matched below).
  // v7.32 — OBJECT-POSITION only: the blanket word test diverted any brief
  // that merely MENTIONS an angle as a topic ("hammer the protein-plus-
  // caffeine angle" — Dan's p3 conquest run fell to the LLM router). The
  // plan path owns the ask only when concepts/angles IS the thing requested.
  if (/\b(?:make|create|generate|build|produce|plan|write|give\s+me|gimme)\s+(?:me\s+)?(?:\d{1,3}\s+)?(?:more\s+)?(?:ad\s+)?(?:concepts?|angles?)\b/.test(m)) return false;
  // v6.96 — "make these ads better" is an improvement ask about existing
  // work, not an order for a fresh batch; it stays conversational.
  if (looksLikeImprovementOfExisting(m)) return false;
  // Advice/question phrasings are not direct campaign requests — route them.
  if (/^\s*(what|which|how|why|when|where|who|should\s+i|do\s+you|any\s+(?:ideas|suggestions)|tell|explain|list)\b/.test(m)) return false;
  // v3.28 — idea / brainstorm / advice asks are CONVERSATIONAL: the user wants
  // suggestions in text, not generated images. "give me some ideas for ads",
  // "ideas for a 30% sale", "brainstorm angles" → let the model answer. Only
  // skip this when the message also gives a concrete "N ads" to actually make
  // (e.g. "make 10 ads with fresh ideas").
  if (/\b(?:ideas?|suggestions?|brainstorm|recommendations?|advice|inspiration)\b/.test(m)
      && !/\b\d{1,3}\s+(?:more\s+)?(?:ads?|creatives?|variations?)\b/.test(m)) {
    return false;
  }
  if (/\?\s*$/.test(m)) return false;
  if (/\b(variation|variations|version|versions|remix|reformat|resize|stories?\s*version|\d+\s*:\s*\d+|of\s+(?:this|these|each|it|that)\b|on\s+(?:this|these|all\s+of\s+these|each)\b|fix\s+(?:this|the|it)\b|redo\s+this|remake\s+this|another\s+one|do\s+another)\b/.test(m)) {
    return false;
  }
  const verb = /\b(make|create|generate|build|produce|design|gimme|give\s+me|need|want|whip\s+up|cook\s+up)\b/;
  const adsNoun = /\b(ads?|creatives?)\b/;
  const nMore = /\b\d{1,3}\s+more\b/;
  if (verb.test(m) && adsNoun.test(m)) return true;
  if (verb.test(m) && nMore.test(m)) return true;
  if (/\b\d{1,3}\s+(?:more\s+)?(?:new\s+|brand[- ]new\s+|winning\s+|ugc\s+|lifestyle\s+)?ads?\b/.test(m)) return true;
  return false;
}

export function looksLikeImprovementOfExisting(msg: string): boolean {
  const m = (msg || "").trim().toLowerCase();
  if (!m) return false;
  // v7.31 — a FRESH-BATCH ORDER is never an improvement ask, whatever
  // trailing words it carries: "make 5 retargeting ads … one of them should
  // be a testimonial-style ad quoting a real customer review" matched
  // "them" + "review" here and got LLM-routed to run_campaign_targeted —
  // skipping the concepts review pause entirely (Dan's Sep 2 catch). An
  // explicit make/create-N-ads head means NEW work; improvement asks about
  // existing work ("make these ads better") carry no count-ads head.
  if (/\b(?:make|create|generate|build|produce|design)\s+(?:me\s+)?\d{1,3}\s+(?:[\w-]+\s+){0,4}ads?\b/.test(m)) return false;
  const refersToExisting =
    /\b(?:these|those|them)\b/.test(m) ||
    /\bthis\s+(?:ad|batch|set|run|creative|image|video|one)\b/.test(m) ||
    /\bthe\s+(?:last|latest|previous|current)\s+\w+/.test(m) ||
    /\bthe\s+(?:ads?|batch|creatives?|images?|videos?)\b/.test(m);
  if (!refersToExisting) return false;
  return /\b(?:better|improve|improving|improvements?|stronger|strengthen|punch(?:ier|y|\s*up)?|tighten|fix(?:es|ing)?|upgrade|level\s*up|refine|polish|critique|feedback|review|rate|compare|versus|vs\.?|which\s+of\s+these|what\s+do\s+you\s+think|how\s+(?:can|could|do|would)\s+(?:we|i|you))\b/.test(m);
}

/** Route requests to the persisted competitor chooser, not prose suggestions. */
export function looksLikeCompetitorGenerationRequest(message:string):boolean {
 const text=generationActionText(message);
 if (isExplicitConversation(message)||defersGeneration(message)||/\b(?:do\s+not|don['’]?t|never)\s+(?:make|create|generate|clone)\b/i.test(text)) return false;
 return /\b(?:make|create|generate|build|produce|clone|do|gimme|give\s+me|want|need)\b/i.test(text)
   && /\bads?\b/i.test(text) && /\bcomp[ei]t\w*(?:'?s)?\b/i.test(text);
}
